import { ITreeGroup, ITreeLeaf, ITreePluginsProps, ITreeStylingParams } from '../../tree.types'
import TreeLeafNode from '../TreeLeafNode'
import FlippingArrow from '../../../Buttons/FlippingArrow'
import { memo } from 'react'
import cn from '../../../../utils/cn'
import SmoothDropdown from '../../../Animated/SmoothDropdownBehaivor/SmoothDropdown'

interface IProps<Leaf extends Record<string, any>, Group extends Record<string, any>, GroupId extends string | number>
  extends ITreePluginsProps {
  group: ITreeGroup<Group, GroupId>

  groupsMap: Record<GroupId, ITreeGroup<Group, GroupId>[]>
  leafsMap: Record<GroupId, ITreeLeaf<Leaf, GroupId>[]>

  renderLeaf: (leaf: ITreeLeaf<Leaf, GroupId>) => JSX.Element | string
  renderGroup: (group: ITreeGroup<Group, GroupId>, isOpen?: boolean) => JSX.Element | string

  onClickLeaf?: (leaf: Leaf) => void
  onClickGroup?: (group: Group) => void

  styling?: ITreeStylingParams
  isRoot?: boolean
}

const TreeGroupNode = <
  Leaf extends Record<string, any>,
  Group extends Record<string, any>,
  GroupId extends string | number
>(
  props: IProps<Leaf, Group, GroupId>
) => {
  const isOpen = props.expandCtrl ? props.expandCtrl.expanded.has(props.group.id) : true
  const normalizedLeafs = props.leafsMap[props.group.id] || []
  const normalizedGroups = props.groupsMap[props.group.id] || []
  const isHasChildren = normalizedLeafs.length || normalizedGroups.length

  const handleClickGroup = () => {
    if (props.leafSelectCtrl) {
      props.leafSelectCtrl.handleSelect(`${props.group.id}-group`)
    }
    if (isHasChildren) {
      props.expandCtrl.toggleItem(props.group.id)
    }
    if (props.onClickGroup) {
      props.onClickGroup(props.group.data)
    }
  }

  const renderGroupChildren = () => {
    if (!isHasChildren) return null

    return (
      <SmoothDropdown className='overflow-inherit' overflow='visible' open={isOpen}>
        {normalizedGroups.map((group) => (
          <TreeGroupNode {...props} isRoot={false} group={group} key={group.id} />
        ))}
        {Boolean(normalizedLeafs.length) && (
          <div className='tree_leafs-content'>
            {normalizedLeafs.map((leaf) => (
              <TreeLeafNode
                onClickLeaf={props.onClickLeaf}
                leaf={leaf}
                leafRenderer={props.renderLeaf}
                key={leaf.key}
                leafSelectCtrl={props.leafSelectCtrl}
                styling={props.styling}
              />
            ))}
          </div>
        )}
      </SmoothDropdown>
    )
  }

  const groupContentClassName = cn('inline-flex-gap gap4 width-100 center', props.styling?.groupClassname)
  const groupContentCustomStyles = props.styling?.groupStyles ?? {}
  return (
    <div className={props.isRoot ? undefined : 'tree_content'}>
      <div
        data-selected={props.leafSelectCtrl && `${props.group.id}-group` === props.leafSelectCtrl.selectedItem}
        className={groupContentClassName}
        onClick={handleClickGroup}
        style={{ cursor: props.expandCtrl ? 'pointer' : 'default', ...groupContentCustomStyles }}
      >
        {props.expandCtrl && <FlippingArrow isOpen={isOpen} />}
        {props.renderGroup(props.group, isOpen)}
      </div>
      {renderGroupChildren()}
    </div>
  )
}

export default memo(TreeGroupNode)
